import {useRef, useState} from 'react'
import type {Dialog} from '@primer/react'
import {useQuery} from '@github-ui/react-query'
import sudo from '@github-ui/sudo'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {usePaths} from '../contexts/PathsContext'
import {IsLoadingContext} from '../contexts/IsLoadingContext'
import {UpdatePreviewsProvider} from '../contexts/UpdatePreviewsContext'
import {useEnforcement} from '../contexts/EnforcementContext'
import {useEditablePreviews} from '../hooks/use-editable-previews'
import {DialogState, type PreviewPayload} from '../types'
import {DeletionDialog} from './deletion-dialog'
import {SelectionDialog} from './selection-dialog'

type DialogWrapperProps = {
  dialogState: DialogState
  setDialogState(state: DialogState): void
  onClose: React.ComponentProps<typeof Dialog>['onClose']
  onSuccess: () => void
}

export function DialogWrapper({dialogState, setDialogState, onClose, onSuccess}: DialogWrapperProps) {
  const createButtonRef = useRef<HTMLButtonElement | null>(null)
  const {createUrl, previewUrl} = usePaths()
  const {
    editablePreviews: previews,
    enabledPreviews,
    enabledRulesets,
    initializePreviews,
    setRulesetName,
    togglePreview,
  } = useEditablePreviews()
  const {enforcement} = useEnforcement()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const {isFetching} = useQuery({
    queryKey: [previewUrl],
    queryFn: async () => {
      const response = await reactFetchJSON(previewUrl)

      if (response.ok) {
        try {
          const data = (await response.json()) as PreviewPayload
          initializePreviews(data)
          return data
        } catch {
          throw new Error('Failed to load previews.')
        }
      } else if (response.status === 408) {
        throw new Error('Your request timed out.')
      } else {
        throw new Error('Failed to load previews.')
      }
    },
    enabled: !!previewUrl,
  })

  async function handleCreateRulesets(shouldDelete: boolean = false) {
    setIsCreating(true)
    setErrorMessage(null)
    if (!(await sudo({target: createUrl, method: 'POST'}))) {
      setErrorMessage('Unauthorized')
      setIsCreating(false)
      setDialogState(DialogState.selection)
      return
    }

    const rulesets = enabledRulesets.map(ruleset => ({...ruleset, enforcement}))
    const body = {
      protected_branch_ids_to_delete: shouldDelete ? enabledPreviews.map(preview => preview.id) : [],
      rulesets,
    }

    const response = await reactFetchJSON(createUrl, {
      method: 'POST',
      body,
    })

    try {
      const data = await response.json()

      if (response.ok) {
        onSuccess()
      } else {
        setErrorMessage(data.message ?? 'Failed to create rulesets.')
        setDialogState(DialogState.selection)
      }
    } catch {
      setErrorMessage('Failed to create rulesets.')
      setDialogState(DialogState.selection)
    } finally {
      setIsCreating(false)
      setDialogState(DialogState.closed)
    }
  }

  return (
    <IsLoadingContext value={isFetching || isCreating}>
      {dialogState === DialogState.selection ? (
        <UpdatePreviewsProvider togglePreview={togglePreview} setRulesetName={setRulesetName}>
          <SelectionDialog
            previews={previews}
            errorMessage={errorMessage}
            onCancel={() => {
              onClose?.('close-button')
              setDialogState(DialogState.closed)
            }}
            onSelection={() => setDialogState(DialogState.deletion)}
            isValid={enabledPreviews.length > 0}
          />
        </UpdatePreviewsProvider>
      ) : (
        <DeletionDialog
          buttonRef={createButtonRef}
          numberOfRulesets={enabledRulesets.length}
          numberOfBranchProtections={enabledPreviews.length}
          onCancel={() => setDialogState(DialogState.selection)}
          onConfirm={handleCreateRulesets}
        />
      )}
    </IsLoadingContext>
  )
}
